import React, { useState } from 'react';
import { Button } from '../../../components/ui/Button/Button';
import { Field } from '../../../components/technician/ui/Field';
import { SegmentedChoice } from '../../../components/technician/ui/SegmentedChoice';
import { Sheet } from '../../../components/technician/ui/Sheet';
import { KEYPAD_FAMILY_SPECS } from '../catalog/family';
import type { KeypadFamilyKey } from '../catalog/family';
import { KEYPAD_CAT6_BRANCHES, KEYPAD_LIMITS } from '../catalog/limits';
import { KEYPAD_FAMILIES } from '../catalog/family';
import { KEYPAD_TYPE_KEYS, KEYPAD_TYPE_SPECS, familyOfType, keyCountOfType, typesOfFamily } from '../catalog/type';
import type { KeypadTypeKey } from '../catalog/type';
import { takenAddresses } from '../commands/keypads';
import { familyOf } from '../model';
import type { Keypad, KeypadDraft } from '../model';
import { useKeypadStore } from '../store';
import { AddressField } from './AddressField';

export interface KeypadEditorSheetProps {
  readonly isOpen: boolean;
  /** The panel being edited, or null when a new one is being added. */
  readonly keypad: Keypad | null;
  readonly onClose: () => void;
  /** Called with the panel that was added, so the console can select it. */
  readonly onAdded: (keypad: Keypad) => void;
}

/** The sheet's own draft of a panel that does not exist yet. */
interface Draft {
  readonly type: KeypadTypeKey;
  readonly name: string;
  readonly address: number;
  readonly cat6Branch: number | null;
  readonly location: string;
}

const defaultType = typesOfFamily(KEYPAD_FAMILIES[0])[0] ?? KEYPAD_TYPE_KEYS[0];

const BRANCH_NONE = '';

/**
 * A panel's own details: what it is, what it is called, where it answers.
 *
 * Adding and editing share one sheet so the fields cannot drift apart. Editing
 * applies as it is typed — a rename that clashes with another panel is refused
 * and said so on the spot — while adding waits for Save, because a half-created
 * panel in the book would be a panel the console would try to write.
 */
export const KeypadEditorSheet: React.FC<KeypadEditorSheetProps> = ({
  isOpen,
  keypad,
  onClose,
  onAdded,
}) => {
  const book = useKeypadStore((store) => store.book);
  const notice = useKeypadStore((store) => store.notice);
  const addKeypad = useKeypadStore((store) => store.addKeypad);
  const renameKeypad = useKeypadStore((store) => store.renameKeypad);
  const moveKeypad = useKeypadStore((store) => store.moveKeypad);
  const updateKeypad = useKeypadStore((store) => store.updateKeypad);
  const forgetAddress = useKeypadStore((store) => store.forgetAddress);
  const clearNotice = useKeypadStore((store) => store.clearNotice);

  const [draft, setDraft] = useState<Draft>({
    type: defaultType,
    name: '',
    address: KEYPAD_LIMITS.ADDRESS_MIN,
    cat6Branch: null,
    location: '',
  });

  const isAdding = keypad === null;

  /**
   * What the fields are editing: the panel itself, or the draft while one is
   * being added. Either way the address rules are the same, so the sheet can say
   * what is in use before anything has been created.
   */
  const type = keypad?.type ?? draft.type;
  const name = keypad?.name ?? draft.name;
  // Not `?? draft.address`: a panel with no address has none, and showing the
  // draft's default would look like it had been given one.
  const address = isAdding ? draft.address : keypad.address;
  const cat6Branch = keypad?.cat6Branch ?? draft.cat6Branch;
  const location = keypad?.location ?? draft.location;
  const family: KeypadFamilyKey = isAdding ? familyOfType(type) : familyOf(keypad);
  const taken = takenAddresses(book, family, keypad?.id ?? null);

  const patch = (changes: Partial<Draft>): void => setDraft({ ...draft, ...changes });

  const save = (): void => {
    const created = addKeypad({
      type: draft.type,
      name: draft.name,
      address: draft.address,
      cat6Branch: draft.cat6Branch,
      location: draft.location,
    } satisfies KeypadDraft);
    if (created.ok) {
      onAdded(created.value);
      onClose();
    }
  };

  return (
    <Sheet
      isOpen={isOpen}
      title={isAdding ? 'Add a panel' : name.trim() === '' ? 'Unnamed panel' : name}
      subtitle={KEYPAD_FAMILY_SPECS[family].label}
      onClose={onClose}
      footer={
        isAdding ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save}>
              Add panel
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        )
      }
    >
      <div className="space-y-5">
        {notice !== null && (
          <div className="flex items-start justify-between gap-3 rounded-xl bg-[#FDECEA] px-4 py-3 text-sm text-[#C0392B]">
            <span>{notice.text}</span>
            <button type="button" onClick={clearNotice} className="font-medium hover:underline">
              Dismiss
            </button>
          </div>
        )}

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-[#333333]">Type</span>
          <select
            value={type}
            onChange={(event) => {
              const type = event.target.value as KeypadTypeKey;
              if (isAdding) patch({ type });
              else updateKeypad(keypad.id, { type });
            }}
            className="h-12 w-full rounded-xl border border-[#E0E0E0] bg-white px-4 text-[#333333] outline-none focus:border-[#D4A054] focus:ring-2 focus:ring-[#D4A054]/30"
          >
            {KEYPAD_FAMILIES.map((familyKey) => (
              <optgroup key={familyKey} label={KEYPAD_FAMILY_SPECS[familyKey].label}>
                {typesOfFamily(familyKey).map((type) => (
                  <option key={type} value={type}>
                    {KEYPAD_TYPE_SPECS[type].label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <span className="mt-1.5 block text-xs text-[#666666]">
            {keyCountOfType(type)} keys, laid out as {KEYPAD_TYPE_SPECS[type].label.toLowerCase()}.
          </span>
        </label>

        <Field
          label="Name"
          value={name}
          onChange={(next) => (isAdding ? patch({ name: next }) : renameKeypad(keypad.id, next))}
          placeholder="e.g. Bedroom 214"
          maxLength={KEYPAD_LIMITS.NAME_MAX}
          hint="How the panel is listed here. The device is never told this."
        />

        <AddressField
          family={family}
          address={address}
          taken={taken}
          onChange={(next) => (isAdding ? patch({ address: next }) : moveKeypad(keypad.id, next))}
        />

        <div>
          <SegmentedChoice
            label="CAT-6 branch"
            value={cat6Branch === null ? BRANCH_NONE : String(cat6Branch)}
            onChange={(next) =>
              isAdding
                ? patch({ cat6Branch: next === BRANCH_NONE ? null : Number(next) })
                : updateKeypad(keypad.id, {
                    cat6Branch: next === BRANCH_NONE ? null : Number(next),
                  })
            }
            options={[
              { value: BRANCH_NONE, label: 'Not recorded' },
              ...KEYPAD_CAT6_BRANCHES.map((branch) => ({
                value: String(branch),
                label: `Branch ${branch}`,
              })),
            ]}
            columns={4}
            hint="Which switch port the panel is patched to. For the fitter's notes; the device is never told this."
          />
        </div>

        <Field
          label="Where it is fitted"
          value={location}
          onChange={(next) =>
            isAdding ? patch({ location: next }) : updateKeypad(keypad.id, { location: next })
          }
          placeholder="e.g. beside the door"
          maxLength={KEYPAD_LIMITS.LOCATION_MAX}
        />

        {keypad !== null && keypad.retiredAddresses.length > 0 && (
          <div className="rounded-xl bg-[#FFF8E8] p-4">
            <p className="text-sm text-[#8A6A2A]">
              This panel used to answer at{' '}
              {keypad.retiredAddresses.join(', ')}. The device may still hold mappings there.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {keypad.retiredAddresses.map((retired) => (
                <Button
                  key={retired}
                  variant="secondary"
                  size="sm"
                  onClick={() => forgetAddress(keypad.id, retired)}
                >
                  Stop tracking {retired}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
};

export default KeypadEditorSheet;
